import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  libraryCompanyPeopleQueryKeys,
  libraryPeopleQueryKeys,
} from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityPerson } from '@/types/library/Entity'

interface UseUploadResumeArgs {
  orgId: UUID | ''
  personId: UUID | ''
}

const uploadResume = async (
  orgId: UUID,
  personId: UUID,
  file: File,
  accessToken: string | undefined | null,
): Promise<EntityPerson> => {
  if (!orgId || !personId) {
    throw new Error('orgId and personId are required')
  }
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }

  const formData = new FormData()
  formData.append('file', file)

  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/people/${personId}/resume`,
    {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return (await res.json()) as EntityPerson
}

export const useUploadResume = ({ orgId, personId }: UseUploadResumeArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  return useMutation<EntityPerson, Error, File>({
    mutationFn: (file: File) =>
      uploadResume(orgId as UUID, personId as UUID, file, accessToken),
    onSuccess: (updated: EntityPerson) => {
      // Update individual person cache
      const personKey = libraryPeopleQueryKeys.person({
        orgId,
        personId: updated.id,
      })
      queryClient.setQueryData(personKey, updated)

      // Update organization people list cache
      const orgListKey = libraryPeopleQueryKeys.organization({ orgId })
      queryClient.setQueryData<PaginatedResponse<EntityPerson>>(
        orgListKey,
        (old) =>
          old
            ? {
                ...old,
                data: old.data.map((p) => (p.id === updated.id ? updated : p)),
              }
            : old,
      )

      // Update any company-specific people lists for this org
      const matchingQueries = queryClient.getQueryCache().findAll({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === 'library_people' &&
          q.queryKey[1] === orgId &&
          q.queryKey.length === 3,
      })

      for (const query of matchingQueries) {
        const key = query.queryKey as ReturnType<
          typeof libraryCompanyPeopleQueryKeys.company
        >
        const data =
          queryClient.getQueryData<PaginatedResponse<EntityPerson>>(key)
        // Only update list-shaped caches
        if (data && data.data) {
          queryClient.setQueryData<PaginatedResponse<EntityPerson>>(key, {
            ...data,
            data: data.data.map((p) => (p.id === updated.id ? updated : p)),
          })
        }
      }

      // Invalidate both individual and org list to refetch fresh data
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: personKey }),
        queryClient.invalidateQueries({ queryKey: orgListKey }),
      ])
    },
    onError: (error) => {
      handleError(error)
    },
  })
}
